import { ciede2000 } from '../color/ciede2000';
import type { BeadColor } from '../catalog/types';
import type { QuantizationResult } from './types';

/**
 * Fusion des couleurs rares (Lot P2 du pivot e-commerce).
 *
 * Politique produit (CDC §4.5) : pas de sachet de 3 perles dans un kit.
 * Toute couleur représentée par moins de `minCount` perles est réassignée à
 * la couleur CONSERVÉE la plus proche (CIEDE2000), et les comptages sont
 * recalculés. Toujours actif dans le tunnel (seuil calibré par preset).
 *
 * Garanties :
 *  - au moins une couleur est toujours conservée (la plus peuplée), même si
 *    toutes sont sous le seuil ;
 *  - les cellules EMPTY (idx < 0) sont ignorées et préservées telles quelles ;
 *  - idempotent : re-appliquer avec le même seuil ne change plus rien ;
 *  - `paletteColorIds` n'est jamais modifié (les couleurs fusionnées tombent
 *    simplement à 0 dans `counts`) — compatibilité éditeur/persistance.
 */
export function mergeRareColors(
  result: QuantizationResult,
  palette: ReadonlyArray<BeadColor>,
  minCount: number,
): QuantizationResult {
  if (palette.length === 0) {
    throw new Error('Fusion impossible : palette vide.');
  }
  if (!Number.isFinite(minCount) || minCount <= 1) return result;

  // Comptage par INDEX (les counts entrants sont par id ; on veut les index).
  const countByIdx = new Array<number>(palette.length).fill(0);
  for (const idx of result.cells) {
    if (idx >= 0 && idx < palette.length) {
      const cur = countByIdx[idx];
      countByIdx[idx] = (cur ?? 0) + 1;
    }
  }

  // Couleurs conservées : count >= minCount. Filet : si aucune, garder la
  // plus peuplée (kit monochrome plutôt que crash).
  const kept: number[] = [];
  for (let i = 0; i < countByIdx.length; i++) {
    if ((countByIdx[i] ?? 0) >= minCount) kept.push(i);
  }
  if (kept.length === 0) {
    let maxIdx = -1;
    let maxCount = 0;
    for (let i = 0; i < countByIdx.length; i++) {
      const c = countByIdx[i] ?? 0;
      if (c > maxCount) {
        maxCount = c;
        maxIdx = i;
      }
    }
    if (maxIdx === -1) return result; // grille entièrement vide
    kept.push(maxIdx);
  }

  // Y a-t-il quelque chose à fusionner ?
  const rare = new Set<number>();
  for (let i = 0; i < countByIdx.length; i++) {
    if ((countByIdx[i] ?? 0) > 0 && !kept.includes(i)) rare.add(i);
  }
  if (rare.size === 0) return result;

  // Mapping rare → conservée la plus proche (CIEDE2000 entre couleurs palette).
  const remap = new Map<number, number>();
  for (const r of rare) {
    const rc = palette[r];
    if (rc === undefined) continue;
    let best = kept[0] ?? 0;
    let bestDist = Infinity;
    for (const k of kept) {
      const kc = palette[k];
      if (kc === undefined) continue;
      const d = ciede2000(rc.lab, kc.lab);
      if (d < bestDist) {
        bestDist = d;
        best = k;
      }
    }
    remap.set(r, best);
  }

  // Réécriture des cellules + recomptage complet.
  const cells = new Array<number>(result.cells.length);
  const counts = new Map<string, number>();
  for (const c of palette) counts.set(c.id, 0);
  for (let i = 0; i < result.cells.length; i++) {
    const idx = result.cells[i] ?? -1;
    const mapped = idx >= 0 ? (remap.get(idx) ?? idx) : idx;
    cells[i] = mapped;
    if (mapped >= 0) {
      const color = palette[mapped];
      if (color !== undefined) {
        counts.set(color.id, (counts.get(color.id) ?? 0) + 1);
      }
    }
  }

  return { width: result.width, height: result.height, cells, counts };
}
